/**
 * Utility to extract and execute scripts from a raw HTML string.
 * This is necessary for third-party integrations (like Iyzico) that return
 * initialization scripts in their HTML payload, as React's dangerouslySetInnerHTML
 * does not execute scripts.
 *
 * @param htmlContent The raw HTML string containing <script> tags.
 * @param container The container element where the non-script HTML should be injected.
 */
export const injectAndExecuteScript = (
  htmlContent: string,
  container: HTMLElement
) => {
  if (!htmlContent || !container) return;

  // 1. Create a temporary container to parse the HTML string
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  // 2. Extract all script elements
  const scripts = tempDiv.querySelectorAll("script");

  // 3. Move non-script nodes to the target container
  // We convert childNodes to an array to avoid live-collection issues during iteration
  const childNodes = Array.from(tempDiv.childNodes);
  childNodes.forEach((node) => {
    // Skip script tags here, we handle them separately
    if (node.nodeName !== "SCRIPT") {
      container.appendChild(node);
    }
  });

  // 4. Manually recreate and inject scripts to force execution
  scripts.forEach((oldScript) => {
    const newScript = document.createElement("script");

    // Copy attributes (src, type, etc.)
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });

    // Copy the inline script content
    if (oldScript.innerHTML) {
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
    }

    // Append to the container (or body) to execute
    container.appendChild(newScript);
  });
};
